import type { EventHandler, FC, MouseEvent, PropsWithChildren } from "react"
import React from "react"

import { Link } from "react-router-dom"
import styled, { css } from "styled-components"

import { fontWeight } from "~/utils/styles"

export const buttonStyle = css`
  --color-fg: var(--blue);
  --color-bg: var(--white);
  font-family: Barlow;
  ${fontWeight(595)};
  color: var(--color-fg);
  background: var(--color-bg);
  border-radius: 5px;
  min-width: 127px;
  height: 30px;
  line-height: 17px;
  text-align: center;
  padding: 2px 10px;
  align-items: center;
  text-align: center;
  justify-content: center;
  display: flex;
  gap: 10px;
  cursor: pointer;
  text-transform: capitalize;
  letter-spacing: 1px;
  &,
  > p {
    font-size: var(--step--2);
  }
  box-sizing: border-box;
  &[type="submit"] {
    --color-fg: var(--white);
    --color-bg: var(--banner-blue);
    border: none;
  }
  &:disabled,
  &.blocked {
    --color-fg: var(--blue-faded);
    cursor: not-allowed;
    &[type="submit"] {
      --color-fg: var(--white);
      --color-bg: var(--blue-faded);
      border: none;
    }
  }
`

export type ButtonVariant = `danger` | `default` | `solid` | `warning`

export const StyledButton = styled.button<{ variant?: ButtonVariant }>`
  ${buttonStyle}
  ${({ variant }) => {
    let variantStyle = undefined
    switch (variant) {
      case `danger`:
        variantStyle = css`
          --color-fg: var(--danger);
          border-color: var(--danger);
          &:disabled {
            --color-fg: var(--danger-faded);
            cursor: not-allowed;
            &[type="submit"] {
              --color-fg: var(--white);
              --color-bg: var(--danger-faded);
              border: none;
            }
          }
        `
        break
      case `warning`:
        variantStyle = css`
          --color-fg: var(--warning);
          border-color: var(--warning);
          &:disabled {
            --color-fg: var(--warning-faded);
            cursor: not-allowed;
            &[type="submit"] {
              --color-fg: var(--white);
              --color-bg: var(--warning-faded);
              border: none;
            }
          }
        `
        break
      case `solid`:
        variantStyle = css`
          --color-fg: var(--white);
          --color-bg: var(--banner-blue);
          border: none;
          &:disabled,
          &.blocked {
            --color-fg: var(--white);
            --color-bg: var(--blue-faded);
            opacity: 0.75;
          }
        `
        break
      default:
        break
    }

    return variantStyle
  }}
`

export type ButtonProps = PropsWithChildren<{
  onClick?: CallableFunction | EventHandler<MouseEvent>
  onMouseUp?: CallableFunction
  type?: `button` | `reset` | `submit`
  style?: React.CSSProperties
  variant?: ButtonVariant
  disabled?: boolean
  form?: string
  blocked?: boolean
}>

const Button: FC<ButtonProps> = ({
  children,
  onMouseUp = (): void => {
    // intentionally left blank
  },
  onClick = (): void => {
    // intentionally left blank
  },
  type = `button`,
  variant = `default`,
  style,
  disabled = false,
  form,
  blocked = false,
}) => (
  <StyledButton
    type={type}
    style={style}
    variant={variant}
    onMouseUp={(e) => {
      onMouseUp(e)
    }}
    onClick={(e) => {
      onClick(e)
    }}
    disabled={disabled}
    form={form}
    className={blocked ? `blocked` : ``}
  >
    {children}
  </StyledButton>
)

export default Button

const StyledButtonLink = styled(Link)`
  ${buttonStyle}
  text-decoration: none;
  user-select: none;
  padding-top: 6px;
`

export type ButtonLinkProps = PropsWithChildren<{
  to: string
  disabled?: boolean
}>

export const ButtonLink: FC<ButtonLinkProps> = ({
  to,
  disabled = false,
  children,
}) =>
  disabled ? (
    <StyledButton
      onClick={() => {
        // intentionally left blank
      }}
      disabled
    >
      {children}
    </StyledButton>
  ) : (
    <StyledButtonLink to={to}>{children}</StyledButtonLink>
  )
